import * as WEBIFC from 'web-ifc'
// Shape-shifters: getItemsData with attributesDefault=true returns
// every IFC attribute either as the raw value, as { value, type }, or
// as a nested entity object. These helpers normalize the surface so
// the UI does not have to special-case every IFC type.

export type IfcValue =
  | { value: unknown; type?: string }
  | string
  | number
  | boolean
  | null
  | undefined
  | Record<string, unknown>

export interface KeyValue {
  key: string
  value: string
  ifcType?: string
  /** O id da propriedade ou quantidade, para ir ler a unidade que ela
   *  declara (o motor de geometria não a guarda). */
  localId?: number
  /** O tipo do valor (IFCLENGTHMEASURE, IFCLABEL, ...) ou a classe da
   *  quantidade (IFCQUANTITYAREA): diz que unidade o valor precisa. */
  medida?: string
  /** 'type' quando o valor vem do Pset do type (herdado), e não da
   *  instância. */
  origem?: 'type'
}

export interface PsetGroup {
  name: string
  ifcClass: string
  rows: KeyValue[]
  /** Set when the group came from the BoQ session's writeLog (psets
   * created by "Escrever no IFC" but not yet round-tripped through a
   * file save+reload). Lets the panel mark them with a subtle "via
   * BoQ" badge so the user can tell them apart from psets that were
   * already in the source file. */
  viaBoQ?: boolean
}

const RELATION_KEYS = new Set([
  'IsDefinedBy',
  'HasAssociations',
  'ContainedInStructure',
  // IFC inverse "ReferencedInStructures" (IfcRelReferencedInSpatialStructure):
  // an element can be referenced (without being contained) in additional
  // spatial structures, e.g. a column that crosses storeys.
  'ReferencedInStructures',
  'IsTypedBy',
  'Decomposes',
  'IsDecomposedBy',
  'HasOpenings',
  'FillsVoids',
  'ConnectedTo',
  'ConnectedFrom',
  'ReferencedBy',
  // Group / system membership: surfaced so the user sees IfcSystem and
  // IfcGroup associations on each element. The corresponding rels are
  // IfcRelAssignsToGroup (groups, systems) and IfcRelServicesBuildings
  // (system → buildings).
  'HasAssignments',
  'ServicedBySystems',
  // Do lado do type: as instâncias (inverso do IfcRelDefinesByType), os
  // property sets e as representações. São listas de entidades, não
  // atributos que se leiam como valor.
  'Types',
  'HasPropertySets',
  'RepresentationMaps',
])

export function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '-'
  if (typeof v === 'string') return v
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '-'
  if (typeof v === 'boolean') return v ? 'true' : 'false'
  if (typeof v === 'bigint') return v.toString()
  if (Array.isArray(v)) return `[${v.length} item(s)]`
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>
    if ('value' in o) return formatValue(o.value)
    if ('Name' in o) return formatValue((o.Name as { value?: unknown })?.value)
    return '[Object]'
  }
  return String(v)
}

export function ifcType(v: unknown): string | undefined {
  if (v && typeof v === 'object' && 'type' in (v as object)) {
    return String((v as { type?: unknown }).type ?? '') || undefined
  }
  return undefined
}

// IFC GlobalId can land under several keys depending on how the model
// was processed. Try them in order and unwrap { value, type } if it's
// the case. Returns null if nothing usable was found.
export function findGuid(item: Record<string, unknown>): string | null {
  const candidates = [
    'GlobalId',
    'globalId',
    'GUID',
    'guid',
    'Guid',
    '_guid',
    '_GlobalId',
  ]
  for (const key of candidates) {
    const v = item[key]
    if (v == null) continue
    if (typeof v === 'string') return v || null
    if (typeof v === 'object' && 'value' in (v as object)) {
      const val = (v as { value?: unknown }).value
      if (typeof val === 'string' && val) return val
    }
  }
  return null
}

export function getCategory(item: Record<string, unknown>): string {
  const cls =
    item._category ??
    item.category ??
    item._class ??
    item.class ??
    item.type ??
    null
  if (cls && typeof cls === 'object' && 'value' in (cls as object)) {
    return String((cls as { value: unknown }).value ?? '')
  }
  return cls ? String(cls) : ''
}

export function getAttributes(item: Record<string, unknown>): KeyValue[] {
  const out: KeyValue[] = []
  for (const [key, value] of Object.entries(item)) {
    if (key.startsWith('_')) continue
    if (RELATION_KEYS.has(key)) continue
    if (value === undefined) continue
    out.push({ key, value: formatValue(value), ifcType: ifcType(value) })
  }
  out.sort((a, b) => a.key.localeCompare(b.key))
  return out
}

function extractPropertyValue(po: Record<string, unknown>): string {
  // IfcPropertySingleValue
  if (po.NominalValue !== undefined) return formatValue(po.NominalValue)
  // IfcPropertyEnumeratedValue
  if (Array.isArray(po.EnumerationValues)) {
    return po.EnumerationValues.map((v) => formatValue(v)).join(', ')
  }
  // IfcPropertyListValue
  if (Array.isArray(po.ListValues)) {
    return po.ListValues.map((v) => formatValue(v)).join(', ')
  }
  // IfcPropertyBoundedValue
  if (
    po.LowerBoundValue !== undefined ||
    po.UpperBoundValue !== undefined ||
    po.SetPointValue !== undefined
  ) {
    const lo = formatValue(po.LowerBoundValue)
    const hi = formatValue(po.UpperBoundValue)
    const sp = formatValue(po.SetPointValue)
    const parts: string[] = []
    if (lo !== '-') parts.push(`min=${lo}`)
    if (hi !== '-') parts.push(`max=${hi}`)
    if (sp !== '-') parts.push(`setpoint=${sp}`)
    return parts.join(', ') || '-'
  }
  // IfcPropertyTableValue
  if (Array.isArray(po.DefiningValues) && Array.isArray(po.DefinedValues)) {
    const rows = po.DefiningValues.length
    return `[tabela ${rows} pares]`
  }
  // IfcPropertyReferenceValue
  if (po.PropertyReference !== undefined) {
    return formatValue(po.PropertyReference)
  }
  // Quantity types fallback for sets that mix qsets and psets
  return formatValue(
    po.LengthValue ??
      po.AreaValue ??
      po.VolumeValue ??
      po.CountValue ??
      po.WeightValue ??
      po.TimeValue,
  )
}

function extractPsetRows(
  pset: Record<string, unknown>,
  pathPrefix = '',
): KeyValue[] {
  const rows: KeyValue[] = []
  // IfcPropertySet → HasProperties[]
  const hasProperties = pset.HasProperties
  if (Array.isArray(hasProperties)) {
    for (const p of hasProperties) {
      if (!p || typeof p !== 'object') continue
      const po = p as Record<string, unknown>
      // Fragments can return a property as an unexpanded stub (only
      // _category/_localId/_guid, no Name/NominalValue) when it fails
      // to serialize its attributes. Such an entry has nothing to show,
      // so skip it instead of rendering a blank row.
      if (po.Name === undefined || po.Name === null) continue
      const name = formatValue(po.Name)
      const fullName = pathPrefix ? `${pathPrefix}.${name}` : name
      const cat = getCategory(po).toUpperCase()
      // IfcComplexProperty has nested HasProperties of its own.
      if (cat.includes('COMPLEX') && Array.isArray(po.HasProperties)) {
        rows.push(...extractPsetRows(po, fullName))
        continue
      }
      const val = extractPropertyValue(po)
      rows.push({
        key: fullName,
        value: val,
        ifcType: ifcType(po.NominalValue) ?? cat ?? undefined,
        localId: numeroOuNulo(po._localId) ?? undefined,
        medida: ifcType(po.NominalValue) ?? ifcType(po.UpperBoundValue) ?? undefined,
      })
    }
  }
  // IfcElementQuantity → Quantities[] (Length/Area/Volume/Count/Weight/Time
  // and IfcPhysicalComplexQuantity which nests HasQuantities).
  const quantities = pset.Quantities
  if (Array.isArray(quantities)) {
    for (const q of quantities) {
      if (!q || typeof q !== 'object') continue
      const qo = q as Record<string, unknown>
      const name = formatValue(qo.Name)
      const fullName = pathPrefix ? `${pathPrefix}.${name}` : name
      const cat = getCategory(qo).toUpperCase()
      if (cat.includes('COMPLEX') && Array.isArray(qo.HasQuantities)) {
        // PhysicalComplexQuantity stores its sub-quantities under
        // HasQuantities, not Quantities. Re-feed extraction with that
        // wrapper.
        rows.push(
          ...extractPsetRows(
            { Quantities: qo.HasQuantities },
            fullName,
          ),
        )
        continue
      }
      const val = formatValue(
        qo.LengthValue ??
          qo.AreaValue ??
          qo.VolumeValue ??
          qo.CountValue ??
          qo.WeightValue ??
          qo.TimeValue,
      )
      rows.push({
        key: fullName,
        value: val,
        ifcType: cat || undefined,
        localId: numeroOuNulo(qo._localId) ?? undefined,
        medida: cat || undefined,
      })
    }
  }
  return rows
}

export function getPsetsAndQsets(item: Record<string, unknown>): {
  psets: PsetGroup[]
  qsets: PsetGroup[]
} {
  const psets: PsetGroup[] = []
  const qsets: PsetGroup[] = []
  const isDefinedBy = item.IsDefinedBy
  if (!Array.isArray(isDefinedBy)) return { psets, qsets }

  for (const rel of isDefinedBy) {
    if (!rel || typeof rel !== 'object') continue
    // The relation IfcRelDefinesByProperties wraps the atual pset in
    // RelatingPropertyDefinition. Some payloads inline it instead.
    const ro = rel as Record<string, unknown>
    const pset = (ro.RelatingPropertyDefinition ?? ro) as Record<
      string,
      unknown
    >
    const cat = getCategory(pset).toUpperCase()
    const group: PsetGroup = {
      name: formatValue(pset.Name) || '(sem nome)',
      ifcClass: cat || 'IFCPROPERTYSET',
      rows: extractPsetRows(pset),
    }
    if (cat.includes('QUANTITY')) qsets.push(group)
    else psets.push(group)
  }
  return { psets, qsets }
}

export interface AssociationGroup {
  ifcClass: string
  name: string
  rows: KeyValue[]
}

export function getMaterialsAndClassifications(
  item: Record<string, unknown>,
): { materials: AssociationGroup[]; classifications: AssociationGroup[] } {
  const materials: AssociationGroup[] = []
  const classifications: AssociationGroup[] = []
  const has = item.HasAssociations
  if (!Array.isArray(has)) return { materials, classifications }

  for (const rel of has) {
    if (!rel || typeof rel !== 'object') continue
    const ro = rel as Record<string, unknown>
    // O motor liga o elemento DIRECTAMENTE ao material ou à
    // classificação, sem a relação pelo meio. Só se procurava a
    // relação, e a secção Materiais dizia 0 em todos os elementos.
    const cat = getCategory(ro).toUpperCase()
    if (!ro.RelatingMaterial && !ro.RelatingClassification) {
      if (cat.startsWith('IFCMATERIAL')) {
        materials.push({
          ifcClass: cat,
          name: formatValue(ro.Name) || '(sem nome)',
          rows: getAttributes(ro),
        })
        continue
      }
      if (cat.startsWith('IFCCLASSIFICATION')) {
        classifications.push({
          ifcClass: cat,
          name: formatValue(ro.Name) || formatValue(ro.Identification) || '(sem nome)',
          rows: getAttributes(ro),
        })
        continue
      }
    }
    if (ro.RelatingMaterial) {
      const m = ro.RelatingMaterial as Record<string, unknown>
      materials.push({
        ifcClass: getCategory(m) || 'IFCMATERIAL',
        name: formatValue(m.Name) || '(sem nome)',
        rows: getAttributes(m),
      })
    } else if (ro.RelatingClassification) {
      const c = ro.RelatingClassification as Record<string, unknown>
      classifications.push({
        ifcClass: getCategory(c) || 'IFCCLASSIFICATIONREFERENCE',
        name:
          formatValue(c.Name) ||
          formatValue(c.Identification) ||
          '(sem nome)',
        rows: getAttributes(c),
      })
    }
  }
  return { materials, classifications }
}

export interface InverseRelationTarget {
  category: string
  name: string
  guid: string | null
  localId: number | null
}

export interface InverseRelationGroup {
  name: string
  targets: InverseRelationTarget[]
}

// Walks the item and returns every IFC inverse relation present in the
// payload. We treat every field whose value is an Array as a relation
// (IFC inverse attributes are always SETs/LISTs). Each target is
// flattened to its identifying fields plus localId so the UI can
// navigate to it.
export function getInverseRelations(
  item: Record<string, unknown>,
): InverseRelationGroup[] {
  const out: InverseRelationGroup[] = []
  for (const [key, value] of Object.entries(item)) {
    if (key.startsWith('_')) continue
    if (!Array.isArray(value)) continue
    if (value.length === 0) continue
    const targets: InverseRelationTarget[] = []
    for (const t of value) {
      if (!t || typeof t !== 'object') continue
      const to = t as Record<string, unknown>
      // Fragments labels the item ID inconsistently across versions /
      // entity types: _localId, localId, expressID or _expressID. Try
      // every common spelling so non-geometric targets (IfcSpace,
      // IfcSystem, IfcGroup) get a clickable popup, not a disabled
      // arrow.
      // For relations whose inverse entry is the REL itself (not the
      // related entity: happens with IfcRelContainedInSpatialStructure
      // for IfcSpace, where Fragments returns the rel with
      // RelatingStructure nested) we also peek inside the common
      // nested fields. The category/name we surface should reflect
      // the related entity, not the rel.
      const nestedTarget =
        (to.RelatingStructure as Record<string, unknown> | undefined) ??
        (to.RelatingObject as Record<string, unknown> | undefined) ??
        (to.RelatingType as Record<string, unknown> | undefined) ??
        (to.RelatingGroup as Record<string, unknown> | undefined) ??
        null
      const display =
        nestedTarget && typeof nestedTarget === 'object' ? nestedTarget : to
      const pickLocalId = (o: Record<string, unknown>): number | null => {
        if (typeof o._localId === 'number') return o._localId
        if (typeof o.localId === 'number') return o.localId
        if (typeof o.expressID === 'number') return o.expressID
        if (typeof o._expressID === 'number') return o._expressID
        if (
          o.expressID &&
          typeof o.expressID === 'object' &&
          typeof (o.expressID as { value?: unknown }).value === 'number'
        ) {
          return (o.expressID as { value: number }).value
        }
        return null
      }
      const localId = pickLocalId(display) ?? pickLocalId(to)
      targets.push({
        category: getCategory(display) || getCategory(to) || '?',
        name: formatValue(display.Name ?? to.Name),
        guid: findGuid(display) ?? findGuid(to),
        localId,
      })
    }
    if (targets.length === 0) continue
    out.push({ name: key, targets })
  }
  out.sort((a, b) => a.name.localeCompare(b.name))
  return out
}

function numeroOuNulo(v: unknown): number | null {
  if (typeof v === 'number') return v
  if (v && typeof v === 'object' && typeof (v as { value?: unknown }).value === 'number') {
    return (v as { value: number }).value
  }
  return null
}

/** As classes de type que o esquema tem, em maiúsculas. Vêm do
 *  web-ifc para não haver uma lista à mão a desactualizar-se. */
let classesDeType: Set<string> | null = null

/** Uma entidade desta classe pode ser definida por um type?
 *
 *  Só essas podem ser "instance-only". Um IfcSite, um piso ou o
 *  projecto não têm type no esquema, e o painel acusava-os de estarem
 *  mal parametrizados. Regra: existe uma classe `<classe>TYPE` (as
 *  StandardCase usam a da classe mãe; portas e janelas do IFC2x3 têm
 *  IfcDoorStyle e IfcWindowStyle). */
export function podeTerType(categoria: string, tipos: Set<string> = obterClassesDeType()): boolean {
  const c = categoria.toUpperCase().replace(/(STANDARDCASE|ELEMENTEDCASE)$/, '')
  if (!c.startsWith('IFC')) return false
  return tipos.has(`${c}TYPE`) || c === 'IFCDOOR' || c === 'IFCWINDOW'
}

function obterClassesDeType(): Set<string> {
  if (!classesDeType) {
    classesDeType = new Set(
      Object.keys(WEBIFC).filter((k) => /^IFC[A-Z0-9]+TYPE$/.test(k) && k !== 'IFCRELDEFINESBYTYPE'),
    )
  }
  return classesDeType
}

export interface TypeInfo {
  category: string
  name: string
  guid: string | null
  localId: number | null
  predefinedType: string
}

// IFC type relation (IfcRelDefinesByType inverse). When non-null the
// element is parametrised by an IfcXxxType; properties on the type
// are shared by every instance referencing it. When null the element
// is "instance-only", a frequent modelling smell because attributes
// then live on each instance instead of being reused.
export function getType(item: Record<string, unknown>): TypeInfo | null {
  const rel = item.IsTypedBy
  if (!Array.isArray(rel) || rel.length === 0) return null
  const first = rel[0]
  if (!first || typeof first !== 'object') return null
  const ro = first as Record<string, unknown>
  const type = (ro.RelatingType ?? ro) as Record<string, unknown>
  // O motor devolve o _localId embrulhado ({ value: 198 }); só se
  // aceitava número, e o "Local ID" do type nunca aparecia.
  const localId = numeroOuNulo(type._localId) ?? numeroOuNulo(type.localId)
  return {
    category: getCategory(type) || '?',
    name: formatValue(type.Name),
    guid: findGuid(type),
    localId,
    predefinedType: formatValue(type.PredefinedType),
  }
}

export interface RepresentationInfo {
  identifier: string
  type: string
  itemCount: number
  isMapped: boolean
  itemCategories: string[]
}

export interface GeometryInfo {
  placementCategory: string
  placementSummary: string
  representations: RepresentationInfo[]
  hasMappedRepresentation: boolean
}

export function getGeometry(item: Record<string, unknown>): GeometryInfo {
  const out: GeometryInfo = {
    placementCategory: '',
    placementSummary: '',
    representations: [],
    hasMappedRepresentation: false,
  }

  const placement = item.ObjectPlacement
  if (placement && typeof placement === 'object') {
    const po = placement as Record<string, unknown>
    out.placementCategory = getCategory(po) || '(referência)'
    if (po.PlacementRelTo) {
      out.placementSummary = 'Relativo a outro placement'
    } else if (po.RelativePlacement) {
      out.placementSummary = 'Placement absoluto'
    } else {
      out.placementSummary = ''
    }
  }

  const rep = item.Representation as Record<string, unknown> | undefined
  if (rep) {
    const reps = rep.Representations as unknown[] | undefined
    if (Array.isArray(reps)) {
      for (const r of reps) {
        if (!r || typeof r !== 'object') continue
        const ro = r as Record<string, unknown>
        const items = ro.Items as unknown[] | undefined
        const itemCategories: string[] = []
        let isMapped = false
        if (Array.isArray(items)) {
          for (const it of items) {
            if (!it || typeof it !== 'object') continue
            const cat = getCategory(it as Record<string, unknown>)
            if (cat) itemCategories.push(cat)
            if (cat.toUpperCase().includes('MAPPED')) isMapped = true
          }
        }
        const repTypeStr = formatValue(ro.RepresentationType).toUpperCase()
        if (repTypeStr.includes('MAPPED')) isMapped = true
        out.representations.push({
          identifier: formatValue(ro.RepresentationIdentifier),
          type: formatValue(ro.RepresentationType),
          itemCount: Array.isArray(items) ? items.length : 0,
          isMapped,
          itemCategories: Array.from(new Set(itemCategories)),
        })
        if (isMapped) out.hasMappedRepresentation = true
      }
    }
  }

  return out
}

export function getLocation(item: Record<string, unknown>): KeyValue[] {
  const cis = item.ContainedInStructure
  if (!Array.isArray(cis) || cis.length === 0) return []
  const out: KeyValue[] = []
  for (const rel of cis) {
    if (!rel || typeof rel !== 'object') continue
    const ro = rel as Record<string, unknown>
    const target = (ro.RelatingStructure ?? ro) as Record<string, unknown>
    const cat = getCategory(target)
    out.push({
      key: cat || 'Container',
      value: formatValue(target.Name) || '(sem nome)',
      ifcType: cat || undefined,
    })
  }
  return out
}
